import React, { useEffect, useState } from 'react';
import { BookOpen, Database, GraduationCap, Plus, ShieldCheck, SlidersHorizontal, Users } from 'lucide-react';
import DashboardLayout from '../components/Dashboard/DashboardLayout';
import StatCard from '../components/Dashboard/StatCard';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabaseClient';

const AdminDashboardPage = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({ totalUsers: 0, totalLessons: 0 });
  const [loading, setLoading] = useState(true);
  const [word, setWord] = useState({ german: '', albanian: '', level: 'C1', chapter: '', module: '' });
  const [wordMessage, setWordMessage] = useState('');

  useEffect(() => {
    let mounted = true;
    const loadStats = async () => {
      const [usersResult, lessonsResult] = await Promise.all([
        supabase.from('profiles').select('id', { count: 'exact', head: true }),
        supabase.from('lessons').select('id', { count: 'exact', head: true }),
      ]);
      if (usersResult.error) console.error('Admin users stats error:', usersResult.error);
      if (lessonsResult.error) console.error('Admin lessons stats error:', lessonsResult.error);
      if (mounted) {
        setStats({
          totalUsers: usersResult.count || 0,
          totalLessons: lessonsResult.count || 0,
        });
        setLoading(false);
      }
    };
    if (user) loadStats();
    return () => { mounted = false; };
  }, [user]);

  const addVocabulary = async (event) => {
    event.preventDefault();
    setWordMessage('');
    const { error } = await supabase.from('admin_vocabulary').insert({
      ...word,
      created_by: user.id,
    });
    if (error) {
      console.error('Vocabulary insert error:', error);
      setWordMessage('Fjala nuk u ruajt. Ekzekuto skemën e re në Supabase.');
      return;
    }
    setWord({ german: '', albanian: '', level: 'C1', chapter: '', module: '' });
    setWordMessage('Fjala u shtua me sukses.');
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex h-[60vh] items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-zinc-200 border-t-yellow-400" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="page-shell-wide space-y-8">
        <header className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
          <div>
            <div className="mb-2 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-amber-600">
              <ShieldCheck size={15} />
              Admin
            </div>
            <h1 className="text-2xl font-extrabold text-zinc-900">Menaxhimi i platformës</h1>
            <p className="mt-1 text-sm text-zinc-500">Kontrollo përmbajtjen dhe progresin e studentëve.</p>
          </div>
          <div className="flex gap-2">
            <Button size="sm" className="gap-2">
              <Plus size={15} /> Shto mësim
            </Button>
            <Button variant="outline" size="sm" className="gap-2">
              <SlidersHorizontal size={15} /> Cilësime
            </Button>
          </div>
        </header>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard label="Studentë" value={stats.totalUsers} icon={Users} />
          <StatCard label="Leksione" value={stats.totalLessons} icon={BookOpen} />
          <StatCard label="Niveli" value="B2" icon={GraduationCap} />
          <StatCard label="Platforma" value="Aktive" icon={Database} />
        </div>

        <Card className="rounded-2xl">
          <CardContent className="flex items-start gap-4 p-6">
            <div className="rounded-xl bg-emerald-50 p-3 text-emerald-600">
              <Database size={22} />
            </div>
            <div>
              <h2 className="font-bold text-zinc-900">Paneli admin është aktiv</h2>
              <p className="mt-1 text-sm leading-relaxed text-zinc-500">
                Statistikat lexohen nga Supabase. Përdor menunë anësore për t&apos;u kthyer te mësimi.
              </p>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-2xl">
          <CardContent className="p-6">
            <h2 className="text-lg font-bold text-zinc-900">Shto fjalor të ri</h2>
            <p className="mt-1 text-sm text-zinc-500">Shto fjalë për C1 ose nivele të tjera.</p>
            <form onSubmit={addVocabulary} className="mt-5 grid gap-3 md:grid-cols-2">
              <input required value={word.german} onChange={(e) => setWord({ ...word, german: e.target.value })} placeholder="Fjala gjermanisht" className="h-10 rounded-lg border px-3 text-sm" />
              <input required value={word.albanian} onChange={(e) => setWord({ ...word, albanian: e.target.value })} placeholder="Përkthimi shqip" className="h-10 rounded-lg border px-3 text-sm" />
              <select value={word.level} onChange={(e) => setWord({ ...word, level: e.target.value })} className="h-10 rounded-lg border bg-white px-3 text-sm">
                <option>A1</option><option>A2</option><option>B1</option><option>B2</option><option>C1</option><option>C2</option>
              </select>
              <input value={word.chapter} onChange={(e) => setWord({ ...word, chapter: e.target.value })} placeholder="Kapitel" className="h-10 rounded-lg border px-3 text-sm" />
              <input value={word.module} onChange={(e) => setWord({ ...word, module: e.target.value })} placeholder="Modul" className="h-10 rounded-lg border px-3 text-sm" />
              <Button type="submit" className="h-10">Ruaj fjalën</Button>
            </form>
            {wordMessage && <p className="mt-3 text-sm font-semibold text-emerald-700">{wordMessage}</p>}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default AdminDashboardPage;
